import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import Login from "../../Authentication/Login";
import "./Invoice.css";
import InvoiceTabs from "./InvoiceTabs";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(dateString).toLocaleDateString("en-US", options);
};

const ExpenseList = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [errors, setErrors] = useState({});
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // New state for branches and selected branch
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");

  const handleOpenDeleteModal = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // Fetch branches
  const GetBranches = async () => {
    try {
      const res = await apiService.getAll(
        `${config.GETBRANCHBYCOMPANY}/${user?.cid}`
      );
      setBranches(res.data || res);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const GetData = async (pageNumber, branchId = null) => {
    // Build query parameters
    const params = {
      pageSize: pageSize,
      pageNumber: pageNumber,
      referenceType: "EXPENSE", // Pass referenceType directly as "EXPENSE"
    };

    // Add branchId to params if provided or if selectedBranch has value
    const branchToUse = branchId !== null ? branchId : selectedBranch;
    if (branchToUse) {
      params.branchId = branchToUse;
    }

    const res = await apiService.getWithParams(
      `${config.GETINVOICEBYUSER}/${user?.id}`, // Use user ID instead of company ID
      params
    );

    setLists(res.data);
    setPageNumber(res.pageNumber);
    setPageSize(res.pageSize);
    setTotalPages(res.totalPages);
    setData(res.data);
    setTotalRecords(res.totalRecords);
  };

  // Handle search button click
  const handleSearch = () => {
    setBtnLoading(true);
    GetData(1); // Reset to first page when searching
    setTimeout(() => setBtnLoading(false), 500);
  };

  // Handle reset button click
  const handleReset = () => {
    setSelectedBranch(""); // Clear branch selection
    GetData(1, ""); // Get all branches data
  };

  const GetLastPageData = () => {
    GetData(totalPages);
  };

  const GetFirstPageData = () => {
    GetData("1");
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    if (pageNumber !== number) GetData(number);
  };

  // Handle branch change
  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => {
    return (
      <li
        className="page-item"
        key={number}
        id={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    );
  });

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETEINVOICE, itemToDelete);
        await GetData(pageNumber);
      } catch (error) {
        // Error toast already handled by apiService
        // Optionally log or track error here
      }
      handleCloseDeleteModal();
    }
  };

  const getStatusBadge = (status, dueDate) => {
    if (status === "PAID") {
      return <span className="badge bg-success">PAID</span>;
    }
    if (status === "UNPAID") {
      return <span className="badge bg-danger">UNPAID</span>;
    }
    return <span className="badge bg-primary">DRAFT</span>;
  };

  const resetForm = () => {
    setBtnLoading(false);
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " form-control-danger" : "";

  useEffect(() => {
    if (user && !loading) {
      GetBranches(); // Fetch branches when user is available
      GetData(pageNumber);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <InvoiceTabs pageName="EXPENSELIST" />
                </div>
                <div className="me-2 pb-1">
                  <div className="row g-4 align-items-center">
                    {/* Branch Dropdown and Buttons */}
                    <div className="col-3">
                      <div className=" ms-3 mb-3">
                        <label htmlFor="branchSelect" className="form-label">
                          Select Branch
                        </label>
                        <select
                          className="form-select"
                          id="branchSelect"
                          value={selectedBranch}
                          onChange={handleBranchChange}
                        >
                          <option value="">All Branches</option>
                          {branches.map((branch) => (
                            <option
                              key={branch.branchId}
                              value={branch.branchId}
                            >
                              {branch.branchName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Search and Reset Buttons with proper spacing */}
                    <div className="col-sm-auto">
                      <div className="mb-3">
                        <label className="form-label d-block">&nbsp;</label>
                        <div className="hstack gap-2">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSearch}
                            disabled={btnLoading}
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={handleReset}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-auto ms-auto">
                      <div className="mb-3">
                        <label className="form-label d-block">&nbsp;</label>
                        <div className="hstack gap-2">
                          <Link
                            to="/expenseinvoice"
                            className="btn btn-primary add-btn"
                          >
                            <i className="ri-add-line align-bottom me-1" /> New
                            Expense Invoice
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive table-card">
                    <table
                      className="table align-middle table-nowrap"
                      id="invoiceTable"
                    >
                      <thead className="table-light">
                        <tr>
                          <th>Invoice No</th>
                          <th>Vendor</th>
                          <th>Invoice Date</th>
                          <th>Due Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody className="list form-check-all">
                        {lists.length > 0 ? (
                          lists.map((v, index) => (
                            <tr key={v.invoiceId}>
                              <td>{v.invoiceNumber}</td>
                              <td>{v.vendorName}</td>
                              <td>{formatDate(v.invoiceDate)}</td>
                              <td>{formatDate(v.dueDate)}</td>
                              <td>{`${v.currencyCode} ${v.grandTotal.toFixed(
                                2
                              )}`}</td>
                              <td>{getStatusBadge(v.status, v.dueDate)}</td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">
                                  <li className="list-inline-item" title="View">
                                    <Link
                                      to={`/expense/view/${v.invoiceId}`}
                                      className="view-item-btn"
                                    >
                                      <i className="ri-eye-fill align-bottom text-muted" />
                                    </Link>
                                  </li>

                                  <li className="list-inline-item" title="Edit">
                                    <Link
                                      to={`/editexpenseinvoice/${v.invoiceId}`}
                                      className="edit-item-btn"
                                    >
                                      <i className="ri-pencil-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                  <li
                                    className="list-inline-item"
                                    title="Delete"
                                  >
                                    <Link
                                      to="#"
                                      className="remove-item-btn"
                                      onClick={() =>
                                        handleOpenDeleteModal(v.invoiceId)
                                      }
                                    >
                                      <i className="ri-delete-bin-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                </ul>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center">
                              No Expense invoices found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing{" "}
                        <span className="fw-semibold">{lists.length}</span> of{" "}
                        <span className="fw-semibold">{totalRecords}</span>{" "}
                        Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li
                        className={
                          "page-item" + data.previousPage === null
                            ? "disabled"
                            : ""
                        }
                        onClick={() => GetFirstPageData()}
                      >
                        <Link className="page-link">Previous</Link>
                      </li>
                      {renderPageNumbers}
                      <li
                        className={
                          "page-item" + data.nextPage === null ? "disabled" : ""
                        }
                        onClick={() => GetLastPageData()}
                      >
                        <Link className="page-link">Next</Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DeleteAlert
            show={showDeleteModal}
            handleClose={handleCloseDeleteModal}
            handleDelete={handleDelete}
          />
        </div>
      </div>
    </div>
  );
};

export default ExpenseList;
